<template>
    <LayoutBlock
        ref="layoutBlock"
        :tutorial_dialog="tutorial_dialog"
        v-on:closeDialog="handleCloseDialog"
        @weightsConfirmed="weightsConfirmed"
        >
        <div class="d-flex flex-column" v-if="measures && updated">
            <div class="pl-2 pb-2 subtitle-1" v-if="conf.title" >{{conf.title}}</div>
            <v-container class="ma-0 pa-0">
                <v-row no-gutters>
                    <v-col cols="4">
                    <NumericField dense outlined
                        :readonly="preview"
                        label="Valore nominale"
                        v-model="measures.nom"
                        :suffix="data.instrument_data.uom"
                    />
                </v-col>
                <v-col cols="4" class="pl-2">
                    <v-btn @click="openWeightSelection(measures.nom, measures.weights)" v-if="!preview">Scegli pesi</v-btn>
                    <v-btn class="ml-4" @click="openWeightSelected([{nom: measures.nom, weights: measures.weights}])" v-if="!preview">Vedi pesi</v-btn>
                </v-col>
                <v-col cols="4" class="d-flex justify-end pl-4">
                    <NumericField dense outlined
                        label="Massa utilizzata"
                        disabled
                        v-model="measures.weightSum"
                        :suffix="data.instrument_data.uom"
                    />
                </v-col>
            </v-row>
            <v-row no-gutters>
                <v-col cols="12" class="d-flex justify-end py-2" v-if="!preview">
                    <v-btn color="primary" @click="autoFillRep(measures.items)">Riempimento automatico</v-btn>
                </v-col>
            </v-row>
            <v-row no-gutters>
                <v-col cols="12">
                    <v-simple-table class="measure_table">
                        <template v-slot:default>
                        <thead class="expansion">
                           <tr>
                                <th class="table-text-blocks text-right">{{ data.block.conf.headers.count }}</th>
                                <th class="table-text-blocks text-right">{{ data.block.conf.headers.zero }} <br/><i class="caption">({{data.instrument_data.uom}})</i></th>
                                <th class="table-text-blocks text-right">{{ data.block.conf.headers.reading }} <br/><i class="caption">({{data.instrument_data.uom}})</i></th>
                            </tr>
                        </thead>
                        <tr v-for="(item,index) of measures.items" :key="index">
                            <td class="table-blocks d-flex justify-center">
                                <v-icon class="d-flex flex-shrink-1 flex-grow-0" v-if="index >= conf.min_readings && !preview" @click="removeRow(index)">delete</v-icon>
                                <div v-else class="d-flex pr-4"></div>
                                <div class="d-flex justify-center flex-shrink-0 flex-grow-1 pr-1">{{index + 1}}</div>
                            </td>
                            <td class="table-blocks">
                                <NumericField dense outlined 
                                    solo hide-details
                                    :readonly="preview" 
                                    @blur="calculateRow(item)"
                                    placeholder="0"
                                    v-model="item.zero"
                                    :negative="true"
                                    :precision="currentPrecision"
                                    ></NumericField>
                            </td>
                            <td class="table-blocks">
                                <NumericField dense outlined 
                                    solo hide-details
                                    :readonly="preview" 
                                    @input="calculateRow(item)"
                                    placeholder="0"
                                    v-model="item.reading"
                                    :precision="currentPrecision"
                                    :class="readingWarning(item.reading)"></NumericField>
                            </td>
                        </tr>
                    </template>
                </v-simple-table>
                </v-col>
                <v-col sm="6" offset-sm="6" class="d-flex flex-grow-1 flex-shrink-0 justify-end">
                    <v-divider class="pl-4" vertical></v-divider>
                    <div class="d-flex flex-column align-self-start my-2 pl-2">
                        <v-checkbox label="Verifica conformità" :disabled="preview" v-model="measures.useTolerance" ></v-checkbox>
                        <NumericField dense outlined
                            v-model="measures.mean"
                            disabled
                            label="media"
                            :negative="true"
                            :suffix="data.instrument_data.uom"
                        />
                        <NumericField dense outlined 
                            v-model="measures.repeatability"
                            disabled
                            :negative="true"
                            :suffix="data.instrument_data.uom"
                            label="ripetibilità"
                        />
                        <NumericField dense outlined
                            v-if="!is_OIML && measures.useTolerance"
                            v-model="measures.repeatabilityFormatted"
                            disabled
                            :negative="true"
                            :suffix="computeToleranceType.uom"
                            label="ripetibilità nel formato della tolleranza"
                        />
                        <span v-if="measures.useTolerance" class="my-2">
                            tolleranza: <i>{{computeToleranceType.type}}, {{computeToleranceType.value}}</i>
                        </span>
                        <NumericField dense outlined
                            v-if="measures.useTolerance"
                            v-model="measures.tolerance"
                            label="valore"
                            disabled
                            :prepend-icon="addWarning"
                            :suffix="computeToleranceType.uom"
                        />
                        <span v-if="measures.useTolerance">
                            <StatusIcon 
                                :status="measures.result" 
                                :tooltip_text="block_tooltip[measures.result]" />
                            {{computedStatus}}
                        </span>
                    </div>
            </v-col>
            </v-row>
            <v-row no-gutters>
                <div class="d-flex flex-column flex-grow-1 flex-shrink-0 table-bordered pa-2">
                        <div v-if="precision_overflow" class="d-flex error--text caption"><StatusIcon status="failed" small /><b>Controlla valori e precisione letture</b></div>
                    <div v-if="precision_overflow" class="error--text caption"><v-icon small color="error">warning</v-icon><b>Controlla valori e precisione letture</b></div>
                </div>        
                <div class="d-flex flex-grow-0 flex-shrink-1 my-2">
                    <v-btn v-if="checkStatic && !preview" color="primary" @click="addRow"><v-icon class="pa-2">add_box</v-icon>aggiungi misura</v-btn>
                </div>
            </v-row>
        </v-container>
        </div>
    </LayoutBlock>
</template>

<script>
import { repeatability_item_scie } from '../../../utils/itemBlockSchema'
import {convertPrimaryWeights} from "../../../utils/commonUtils"
import {BaseIso} from "../../../utils/baseIso"
// import _ from 'lodash'
import LayoutBlock from "../../../components/utils/LayoutBlock"
import StatusIcon from "../../../components/utils/StatusIcon"
import Big from "big.js"

export default {
    components: {LayoutBlock, StatusIcon},
    mixins: [BaseIso],
    
    computed: {
        zero_overflow(){
            for (let elm of this.measures.items) {
                if (!this.checkPrecision(elm.zero)) {
                    return true
                }
            }
            return false
        },
        
    },

    watch: {
        "measures.items": {
            deep:true,
            handler(){
                if(!this.preview){
                    console.log('WATCHER NAWI REPEATIBILITY')
                    this.calcRepeatibility()
                }
            }
        }
    },
    
    methods: {
        calculateRow(item){
            if(item.reading && item.zero){
                item.difference = this.round(Big(item.reading).minus(item.zero))
                this.calcMean()
                this.calculateMeanDifference()
            }
        },
        addRow(){
            let item = repeatability_item_scie()
            this.measures.items.push(item)
        },
        calcMean(){
            let temp = Big(0)
            for(let e of this.measures.items){
                if(e.difference){
                    temp = temp.plus(e.difference)
                }
            }
            this.measures.mean = this.round(temp.div(this.measures.items.length), this.precision)
        },
        calculateMeanDifference(){
            for(let elem of this.measures.items){
                if(elem.difference){
                    elem.mean_difference = this.round(Big(elem.difference).minus(this.measures.mean))
                    elem.square_mean_difference = this.round(Big(elem.mean_difference).pow(2), this.currentPrecision*2)
                }
            }
        },
       
        async buildMeasures(){
            this.measures = {
                nom: null,
                mean: "0",
                repeatability: "0",
                repeatabilityFormatted: "0",
                weights: [],
                weightSum: "0",
                items: [],
                result: 'uncomplete',
                useTolerance: false,
                tolerance: null,
                toleranceData: null
            }
            this.buildToleranceVal()
            for(let i = 0; i< this.data.block.conf.min_readings; i++){
                let item = repeatability_item_scie()
                this.measures.items.push(item)
            }
            this.measures.nom = await this.buildWithCategory()
            if(this.measures.nom){
                let autoSelected = this.autoSelection(this.measures.nom)
                if(autoSelected.length > 0){
                    this.weightsConfirmed(autoSelected)
                }
                // this.autoSelection(this.measures.nom)
            }
        },
    
        calculateResult(isComplete){
            console.log("stampa isComplete", isComplete)
            let rep = null
            if(this.is_OIML){
                rep = this.measures.repeatability
            } else {
                rep = this.measures.repeatabilityFormatted
            }
            if (!isComplete) {
                this.measures.result = 'uncomplete'
            } else {
                if(rep && this.measures.tolerance){
                    this.measures.result = !this.precision_overflow && Big(rep).lte(this.measures.tolerance) ? 'done': 'failed'
                } else {
                    this.measures.result = 'failed'
                }
            }
        },
        calcRepeatibility(){
            let sum = Big(0)
            let isComplete = true
            for(let elm of this.measures.items){
                if ( elm.reading== null || elm.reading == '' || elm.zero == null || elm.zero == '') {
                    isComplete = false
                }

                if(elm.square_mean_difference){
                    sum = sum.plus(elm.square_mean_difference)
                }
            }
            this.measures.repeatability = this.round(sum.div(this.measures.items.length - 1).sqrt(), this.precision*2)
            if(!this.is_OIML){
                this.measures.repeatabilityFormatted = this.handleToleranceError()
            }
            this.calculateResult(isComplete)            
        },

        handleToleranceError(){
            if(this.measures.repeatability && this.measures.toleranceData && this.measures.nom){
                if(this.calcMinRange(this.measures.nom)){
                    switch(this.measures.toleranceData.t_calc){
                        case "perc": return this.round(Big(this.measures.repeatability).div(this.measures.nom).times(100).abs(), this.precision*4)
                        case "val": return Big(this.measures.repeatability).div(this.calcMinRange(this.measures.nom).d).abs().toFixed(0)
                    }
                } else {
                    return "0"
                }
            }
        },

        handleToleranceChange(){
            for(let item of this.measures.items){
                item.toleranceError = this.handleToleranceError(item)
            }
        },

        calcWeightSum(weights){
            let weightSum = weights.reduce((a,b) => Big(a).plus(b.conv).toString(),0)
            this.measures.weightSum = weightSum
            if(this.is_OIML){
                this.measures.tolerance = this.calc_OIML(this.calcMinRange(weightSum), weightSum)
            }
        },
        weightsConfirmed(weights){
            this.measures.weights = _.cloneDeep(weights)
            let items = convertPrimaryWeights(weights, _.cloneDeep(this.data.primaries), this.instrument_data.uom)
            this.measures.nom = items.reduce((a,b) => Big(a).plus(b.name).toString(),0)
            // console.log("$$ debug items", items)
            this.calcWeightSum(items)
            for(let item of this.measures.items){
                item.nom = this.measures.weightSum
            }
        },
        saveMeasures() {
            // console.log("$$ DEBUG save measures rep scie")
            let block = this.data.block
            this.data.common_data[block.name][block._id].value = this.measures.repeatability
            this.$parent.$emit('updateMeasure', {b_index: this.data.b_index, measures: this.measures, p_idx: this.data.p_idx, p_id: this.data.p_id, common_data: this.data.common_data })
        },
        handleInvalidPrimaries(){
            if(this.data.primaries && this.data.invalidPrimaries){
                // console.log("$$ resetting repeatability")
                this.measures.weightSum = 0
                this.measures.weights.splice(0,this.measures.weights.length)
            }
        },
        autoFill(items){
            for (let item of items){
                this.$set(item, 'reading', item.nom)
                this.$set(item, 'zero', this.round(0))
            }
        },
        autoFillRep(items){
            this.autoFill(items)
            for(let item of items){
                this.calculateRow(item)
            }
        }


    },
  
    
}
</script>